import { Button, ButtonProps, Div, Image, P } from '@kizi/core-components';
import {
  IconV2 as Icon,
  ModalProps,
  TabDefinition,
  TabView,
} from '@kizi/shared-components';
import React, { useContext } from 'react';
import { PriceProps, sendCustomEvent } from '.';
import { COMPARATOR_TYPE } from '../funnels';
import { EnergyProduct, getProductProps, HomeProduct } from '../utils/product';
import { getLabel } from '../utils/translations';
import { Price } from './Price';
import { ProductDetails } from './ProductDetails';
import { ProductDetailsTariffs } from './ProductDetailsTariffs';
import {
  ProductSupplierDetails,
  ProviderContent,
} from './ProductSupplierDetails';
import { ProductOverviewModalHeader } from './ProductOverviewModalHeader';
import { Window } from './Window';
import { ComparatorContext, IProduct, KiziLogger } from '@kizi/comparator-core';

type ModalCloseButtonProps = ButtonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement>;

export const ModalCloseButton: React.FC<ModalCloseButtonProps> = (props) => {
  return (
    <Button
      position="absolute"
      top="2"
      right="2"
      type="button"
      variant="transparent"
      {...props}
    >
      X
    </Button>
  );
};

interface ProductTabViewProps {
  startIndex: number;
  tabs: any[];
}

export const ProductTabView: React.FC<ProductTabViewProps> = ({
  startIndex,
  tabs,
}: ProductTabViewProps) => {
  return (
    <TabView
      display="flex"
      flexDirection="column"
      startIndex={startIndex}
      tabs={tabs}
      tabProps={(selected) => ({
        variant: `tabViewTab${selected ? 'Selected' : ''}`,
      })}
    >
      {(Tabs, Content) => {
        return (
          <>
            <Div variant="tabViewTabsContainerIndented">{Tabs}</Div>
            <Div variant="tabViewContentIndented">{Content}</Div>
          </>
        );
      }}
    </TabView>
  );
};

interface TopPanelProps {
  providerName: string;
  providerImageSource: string;
  priceProps: PriceProps;
  onClose: React.MouseEventHandler;
  onSubmit: React.MouseEventHandler;
  purchasableProduct: boolean;
  isOverstappen: boolean;
}

export const TopPanel: React.FC<TopPanelProps> = ({
  providerName,
  providerImageSource,
  priceProps,
  onClose,
  onSubmit,
  purchasableProduct,
  isOverstappen,
}) => {
  return (
    <>
      <Image alt={providerName} src={providerImageSource} maxHeight={38} />
      <Div
        display="flex"
        flexDirection={['column', null, 'row']}
        alignItems={['flex-end', null, '']}
        justifyContent="space-between"
      >
        <Price {...priceProps} variant="priceMedium" mr={[null, 3]} my="auto" />
        {purchasableProduct ? (
          <Button
            type="button"
            variant="primarySmall"
            onClick={onSubmit}
            my="auto"
          >
            {isOverstappen ? 'Aanvragen' : 'Ga verder'}
          </Button>
        ) : (
          <Div variant="unavailablePriceContainer">
            <P variant="secondaryCaptionBold">
              Geen samenwerking met verzekeraar
            </P>
          </Div>
        )}
        <Div display="flex" ml={4} mb="auto">
          <Icon
            display="flex"
            fill="second.0"
            height={20}
            name="cancel"
            onClick={onClose}
            style={{
              cursor: 'pointer',
            }}
            width={20}
          />
        </Div>
      </Div>
    </>
  );
};

export type ProductOverviewModalProps = Omit<ModalProps, 'children'> & {
  startIndex: number;
  priceMode: 'month' | 'year';
  product: EnergyProduct | HomeProduct | IProduct;
  onSubmit: React.MouseEventHandler<HTMLButtonElement>;
};

type ContentResponse = {
  postcoderoosregeling: string;
  providerDescription: string;
  providerId: string;
  providerImageSource: string;
  providerName: string;
  priceProps: PriceProps;
  tabs: TabDefinition[];
  firstTabText: string;
};

function getContent(product, priceMode: 'month' | 'year'): ContentResponse {
  const defaultReturnedObject: ContentResponse = {
    postcoderoosregeling: '',
    providerDescription: '',
    providerId: '',
    providerImageSource: '',
    providerName: '',
    priceProps: { value: 0 },
    tabs: [],
    firstTabText: '',
  };

  const { type } = product;

  if (type.includes('energy')) {
    const { provider, price } = product;
    const {
      attributes,
      description: providerDescription,
      id: providerId,
      name: providerName,
    } = provider;
    const { postcoderoosregeling } = attributes;

    const priceProps = {
      discount: price[priceMode].discountIncluded,
      label: `per ${getLabel(priceMode)}`,
      value: price[priceMode].total,
    };
    const providerImageSource = product.image;

    let tabs = [
      {
        label: 'Productdetails',
        content: <ProductDetails data={product} priceMode={priceMode} />,
      },
      {
        label: 'Tarieven',
        content: <ProductDetailsTariffs data={product} priceMode={priceMode} />,
      },
    ];

    return {
      postcoderoosregeling,
      providerDescription,
      providerId,
      providerImageSource,
      providerName,
      priceProps,
      tabs,
      firstTabText: 'Over leverancier',
    };
  } else if (type === COMPARATOR_TYPE.HOME) {
    const { companyNameDescription } = product;

    const providerName = companyNameDescription;

    const { initialValue, priceLabel, total } = getProductProps(
      product,
      priceMode,
    );

    const tabs = [
      {
        label: 'Vergoedingen',
        content: <div />,
      },
      {
        label: 'Beoordelingen',
        content: <div />,
      },
    ];

    return {
      ...defaultReturnedObject,
      providerName,
      priceProps: {
        initialValue,
        label: priceLabel,
        value: total,
        variant: 'priceMedium',
      },
      tabs,
    };
  } else return defaultReturnedObject;
}

export const ProductOverviewModal: React.FC<ProductOverviewModalProps> = ({
  startIndex,
  product,
  priceMode,
  onClose,
  onSubmit,
  ...restProps
}) => {
  const show = startIndex > -1;
  if (!show) return null;
  sendCustomEvent('productoverview-modal', {});

  const purchasableProduct =
    (product as any)?.availability?.acceptance?.accepted !== false;

  const [localTabs, setLocalTabs] = React.useState<TabDefinition[] | undefined>(
    undefined,
  );
  const [providerData, setProviderData] = React.useState<
    ProviderContent | undefined
  >(undefined);

  const onApplyClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClose(e);
      onSubmit(e);
    },
    [onClose, onSubmit],
  );

  const {
    providerDescription,
    providerId,
    providerImageSource,
    providerName,
    priceProps,
    postcoderoosregeling,
    tabs,
    firstTabText,
  } = getContent(product, priceMode);

  const { name } = useContext(ComparatorContext);
  const isOverstappen = name.includes('os-');

  React.useEffect(() => {
    if (providerId) {
      fetch(
        `${process.env.KIZI_PUBLIC_API_BASE_URL}/${
          product.type === 'energyAuction' ? 'energy' : product.type
        }/products?provider=${providerId}`,
        {
          method: 'GET',
          headers: {
            'Content-type': 'application/json',
            Authorization: `Basic ${process.env.KIZI_PUBLIC_API_AUTH_TOKEN}`,
          },
        },
      )
        .then((response) => response.json())
        .then((body) => {
          setProviderData(body['data'][0]);
        })
        .catch((error) => {
          KiziLogger.logError({
            eventId: 0,
            member: 'ProductOverviewModal',
            message: 'Error in fetchProviderData',
            eventName: 'fetchProviderDataError',
            data: {
              error,
            },
          });
        });
    }
  }, [providerId]);

  React.useEffect(() => {
    if (providerData) {
      setLocalTabs([
        {
          label: firstTabText || 'Over verzekeraar',
          content: (
            <ProductSupplierDetails
              content={providerData}
              description={providerDescription}
              postcoderoosregeling={postcoderoosregeling}
            />
          ),
        },
        ...tabs,
      ]);
    }
  }, [providerData]);

  if (!localTabs) return null;

  const content = (
    <>
      <Div>
        <Div display={['none', null, 'flex']} variant="productModalTopPanel">
          <TopPanel
            providerName={providerName}
            providerImageSource={providerImageSource}
            priceProps={priceProps}
            onClose={onClose}
            onSubmit={onSubmit}
            purchasableProduct={purchasableProduct}
            isOverstappen={isOverstappen}
          />
        </Div>
      </Div>
      <Div bg="white" borderBottomRightRadius={2} borderBottomLeftRadius={2}>
        <ProductTabView tabs={localTabs} startIndex={startIndex} />
      </Div>
    </>
  );

  return (
    <Window
      bottomContent={
        <ProductOverviewModalHeader
          priceProps={priceProps}
          purchasableProduct={purchasableProduct}
          onApplyClick={onApplyClick}
          isOverstappen={isOverstappen}
        />
      }
      modalProps={{
        noCloseIcon: true,
        noHorizontalMargin: true,
        children: content,
      }}
      noHorizontalPadding
      type="modal"
      title={
        <Image
          alt={providerName}
          height={28}
          src={providerImageSource}
          width="auto"
        />
      }
      onClose={onClose}
      show={show}
      {...restProps}
    >
      {content}
    </Window>
  );
};
